import React, { useEffect, useState, useContext, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Chip,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { WarningAmber, Close } from "@mui/icons-material";
import Shop from "../shop";
import { AppContext } from "../../context/AppContext";
import ImageGallery from "./ProductPage/ImageGallery";
import RelatedProduct from "./ProductPage/RelatedProduct";

export default function ProductDetail() {
  const { sectionSlug, categorySlug, productSlug, variantSlug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useContext(AppContext);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showAlert, setShowAlert] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [galleryImgs, setGalleryImgs] = useState([]);

  const formatDescription = (text) => {
    if (!text) return "";
    return String(text)
      .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
      .replace(/_(.*?)_/g, "<em>$1</em>")
      .replace(/~(.*?)~/g, "<s>$1</s>");
  };

  const toNum = (v, d = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
  };

  const applyPromoNet = (base, tipo, valor) => {
    const b = toNum(base, 0);
    const v = toNum(valor, 0);
    if (!tipo) return b;
    if (tipo === "descuento" && v > 0) return Math.max(b - b * (v / 100), 0);
    if (tipo === "precio_fijo" && v > 0) return Math.max(b - v, 0);
    return b;
  };

  const round2 = (n) => Math.round(toNum(n, 0) * 100) / 100;

  const calcIva = (net, pct) => {
    const p = toNum(pct, 0);
    if (p === 999) return 0;
    return round2(toNum(net, 0) * (p / 100));
  };

  const pickPromoFromData = (data) => {
    const tipo =
      data?.tipo_promocion ??
      data?.tipoPromocion ??
      data?.promocion ??
      data?.promo?.tipo ??
      null;

    const valorRaw =
      data?.valor_promocion ??
      data?.valorPromocion ??
      data?.descuento ??
      data?.promo?.valor ??
      null;

    const valor = valorRaw === null || valorRaw === undefined ? null : toNum(valorRaw, 0);
    return { tipo, valor };
  };

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const slugToFetch = variantSlug || productSlug;
        const res = await fetch(`/productos/ver/${slugToFetch}/`);
        const data = await res.json();

        const promo = pickPromoFromData(data);

        const variants = Array.isArray(data?.variants) ? data.variants : [];

        const base = {
          id: data.id,
          code: data.code,
          name: data.name,
          slug: data.slug,
          section: data.section,
          category: data.category,
          brand: data.brand,
          description: data.description || "",
          images: Array.isArray(data.images) ? data.images : [],
          pvp1: toNum(data.pvp1, 0),
          precio_descuento: data.precio_descuento != null ? toNum(data.precio_descuento, 0) : null,
          iva_porcentaje: toNum(data.iva_porcentaje, 0),
          promo,
          type: data.type,
          variants: variants.map((v) => {
            const vPromo = pickPromoFromData(v);
            return {
              ...v,
              id: v.id,
              slug: v.slug,
              sku: v.sku,
              value: v.value,
              variant_type: v.variant_type,
              image: v.image,
              price: toNum(v.price, 0),
              precio_descuento: v.precio_descuento != null ? toNum(v.precio_descuento, 0) : null,
              iva_porcentaje: toNum(v.iva_porcentaje ?? data.iva_porcentaje, 0),
              promo: vPromo.tipo ? vPromo : promo,
            };
          }),
        };

        setProduct(base);

        const currentVariantSlug = data?.type === "variant" ? data.slug : variantSlug || null;

        if (base.variants.length && currentVariantSlug) {
          const found = base.variants.find((x) => String(x.slug) === String(currentVariantSlug)) || null;
          setSelectedVariant(found);
          setGalleryImgs(found?.image ? [found.image, ...base.images] : base.images);
        } else {
          setSelectedVariant(null);
          setGalleryImgs(base.images);
        }
      } catch {
        setProduct(null);
      } finally {
        setLoading(false);
      }
    }
    window.scrollTo(0, 0);
    load();
  }, [productSlug, variantSlug]);

  const priceState = useMemo(() => {
    if (!product) {
      return {
        hasVariants: false,
        hasDiscount: false,
        baseNet: 0,
        finalNet: 0,
        ivaPct: 0,
        ivaBase: 0,
        ivaFinal: 0,
        unitOriginalWithIva: 0,
        unitPriceWithIva: 0,
        total: 0,
      };
    }

    const hasVariants = product.variants.length > 0;

    const baseNet = selectedVariant ? toNum(selectedVariant.price, 0) : toNum(product.pvp1, 0);

    const promo = selectedVariant?.promo?.tipo ? selectedVariant.promo : product.promo;

    const finalNet =
      selectedVariant
        ? selectedVariant.precio_descuento != null
          ? toNum(selectedVariant.precio_descuento, 0)
          : applyPromoNet(baseNet, promo.tipo, promo.valor)
        : product.precio_descuento != null
          ? toNum(product.precio_descuento, 0)
          : applyPromoNet(baseNet, promo.tipo, promo.valor);

    const ivaPct = selectedVariant
      ? toNum(selectedVariant.iva_porcentaje ?? product.iva_porcentaje, 0)
      : toNum(product.iva_porcentaje, 0);

    const ivaBase = calcIva(baseNet, ivaPct);
    const ivaFinal = calcIva(finalNet, ivaPct);

    const unitOriginalWithIva = round2(baseNet + ivaBase);
    const unitPriceWithIva = round2(finalNet + ivaFinal);

    const hasDiscount = finalNet < baseNet - 1e-9;

    const total = round2(unitPriceWithIva * toNum(quantity, 1));

    return {
      hasVariants,
      hasDiscount,
      baseNet,
      finalNet,
      ivaPct,
      ivaBase,
      ivaFinal,
      unitOriginalWithIva,
      unitPriceWithIva,
      total,
      promo,
    };
  }, [product, selectedVariant, quantity]);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );

  if (!product)
    return (
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <Typography variant="h5" color="error">
          Producto no encontrado
        </Typography>
      </Box>
    );

  const adjustQty = (d) => setQuantity((q) => Math.max(1, toNum(q, 1) + d));

  const addToCartHandler = () => {
    if (priceState.hasVariants && !selectedVariant) return;

    const url = selectedVariant
      ? `/categorias/${sectionSlug}/${categorySlug}/${productSlug}/${selectedVariant.slug}`
      : `/categorias/${sectionSlug}/${categorySlug}/${productSlug}`;

    const promo = priceState.promo || product.promo;

    const item = selectedVariant
      ? {
          id: `${product.id}-${selectedVariant.id}`,
          name: product.name,
          code: product.code,
          sku: selectedVariant.sku ?? "",
          price_with_iva: Number(priceState.unitPriceWithIva),
          quantity,
          section: product.section,
          category: product.category,
          brand: product.brand,
          images: selectedVariant.image
            ? [selectedVariant.image, ...(product.images || [])]
            : product.images || [],
          variant: { type: product.variants?.[0]?.variant_type, value: selectedVariant.value },
          variantUrl: url,
          promocion: promo?.tipo ?? null,
          descuento: promo?.valor != null ? Number(promo.valor) : 0,
          iva_porcentaje: Number(priceState.ivaPct),
          iva_valor: Number(priceState.ivaFinal),
        }
      : {
          id: product.id,
          name: product.name,
          code: product.code,
          price_with_iva: Number(priceState.unitPriceWithIva),
          quantity,
          section: product.section,
          category: product.category,
          brand: product.brand,
          images: product.images || [],
          variantUrl: url,
          promocion: promo?.tipo ?? null,
          descuento: promo?.valor != null ? Number(promo.valor) : 0,
          iva_porcentaje: Number(priceState.ivaPct),
          iva_valor: Number(priceState.ivaFinal),
        };

    addToCart(item, quantity);
    setIsCartOpen(true);
  };

  const selectVariant = (v) => {
    if (!v) return;
    setSelectedVariant(v);
    setQuantity(1);
    setGalleryImgs(v?.image ? [v.image, ...product.images] : product.images);
    navigate(`/categorias/${sectionSlug}/${categorySlug}/${productSlug}/${v.slug}`, { replace: true });
  };

  const promoChip = () => {
    const promo = priceState.promo || product.promo || { tipo: null, valor: null };
    const tipo = promo?.tipo ?? null;
    const valor = promo?.valor;

    const commonSx = {
      bgcolor: "#000",
      color: "#fff",
      fontWeight: 800,
      position: "absolute",
      top: 10,
      left: 10,
      zIndex: 50,
      boxShadow: "0 6px 14px rgba(0,0,0,0.25)",
      borderRadius: "16px",
      px: 1.2,
      py: 0.2,
      fontSize: "0.85rem",
    };

    if (tipo === "descuento" && toNum(valor, 0) > 0) {
      return <Chip label={`Descuento ${Math.round(toNum(valor, 0))}%`} sx={{ ...commonSx, bgcolor: "#d32f2f" }} />;
    }
    if (tipo === "precio_fijo" && toNum(valor, 0) > 0) {
      return <Chip label="Oferta" sx={{ ...commonSx, bgcolor: "#ed6c02" }} />;
    }
    if (tipo === "destacado") {
      return <Chip label="Destacado" sx={{ ...commonSx, bgcolor: "#1976d2" }} />;
    }
    if (tipo === "mas_vendido") {
      return <Chip label="Más vendido" sx={{ ...commonSx, bgcolor: "#2e7d32" }} />;
    }
    return null;
  };

  return (
    <>
      <Box sx={{ p: 4, maxWidth: 1200, mx: "auto", bgcolor: "#fff" }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ position: "relative" }}>
              {promoChip()}
              <ImageGallery images={galleryImgs} productName={product.name} />
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Typography variant="h5" sx={{ color: "#000", mb: 1 }}>
              {product.name}
            </Typography>

            <Typography variant="caption" sx={{ color: "#666", display: "block", fontSize: "0.75rem", mb: 1 }}>
              REF: {product.code}
            </Typography>

            {selectedVariant?.sku && (
              <Typography variant="caption" sx={{ color: "#666", mb: 1, display: "block", fontSize: "0.75rem" }}>
                SKU: {selectedVariant.sku}
              </Typography>
            )}

            <Typography variant="body1" sx={{ color: "#666", mb: 1 }}>
              {product.brand}
            </Typography>

            <Typography variant="body2" sx={{ color: "#999", mb: 2 }}>
              Precio incluye IVA
            </Typography>

            {priceState.hasVariants && (
              <>
                <Typography variant="body2" sx={{ color: "#999", mb: 1 }}>
                  {product.variants[0]?.variant_type}
                </Typography>

                <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                  <Select
                    value={selectedVariant?.id || ""}
                    displayEmpty
                    onChange={(e) => {
                      const v = product.variants.find((x) => String(x.id) === String(e.target.value));
                      selectVariant(v);
                    }}
                    renderValue={(sel) =>
                      !sel ? (
                        <Typography sx={{ color: "#999" }}>
                          Seleccionar {product.variants[0]?.variant_type}
                        </Typography>
                      ) : (
                        product.variants.find((v) => String(v.id) === String(sel))?.value
                      )
                    }
                  >
                    <MenuItem value="" disabled>
                      Seleccionar {product.variants[0]?.variant_type}
                    </MenuItem>
                    {product.variants.map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        {v.value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            {(!priceState.hasVariants || selectedVariant) && (
              <>
                {priceState.hasDiscount && (
                  <Typography variant="body1" sx={{ color: "#9e9e9e", textDecoration: "line-through" }}>
                    $ {priceState.unitOriginalWithIva.toFixed(2)}
                  </Typography>
                )}

                <Typography variant="h4" sx={{ color: "#1565C0", fontWeight: 700, mb: 2 }}>
                  $ {priceState.unitPriceWithIva.toFixed(2)}
                </Typography>
              </>
            )}

            <Typography variant="body2" sx={{ color: "#999", mb: 1 }}>
              Cantidad
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <IconButton onClick={() => adjustQty(-1)} sx={{ bgcolor: "#f0f0f0" }}>
                <RemoveIcon />
              </IconButton>
              <Typography variant="h6">{quantity}</Typography>
              <IconButton onClick={() => adjustQty(1)} sx={{ bgcolor: "#f0f0f0" }}>
                <AddIcon />
              </IconButton>
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="body2" sx={{ color: "#999", mb: 1 }}>
              Precio Total
            </Typography>

            <Typography variant="h5" sx={{ color: "#1565C0", mb: 0.5 }}>
              $ {priceState.total.toFixed(2)}
            </Typography>

            <Typography variant="caption" sx={{ color: "#666", display: "block", mb: 2 }}>
              Precio incluye IVA
            </Typography>

            <Button
              variant="contained"
              sx={{
                bgcolor: "#0A3D91",
                color: "#fff",
                py: 1,
                mb: 2,
                width: { xs: "100%", md: "80%" },
                "&:hover": { bgcolor: "#0D6EFD" },
              }}
              startIcon={<ShoppingCartIcon />}
              disabled={priceState.hasVariants && !selectedVariant}
              onClick={addToCartHandler}
            >
              Añadir al Carrito
            </Button>
          </Grid>
        </Grid>

        {showAlert && (
          <Box
            sx={{
              p: 2,
              mt: 6,
              mb: 3,
              border: "1px solid #ffb74d",
              backgroundColor: "#fff3e0",
              borderRadius: 2,
              display: "flex",
              alignItems: "flex-start",
              position: "relative",
            }}
          >
            <WarningAmber sx={{ color: "#ff9800", mr: 1, mt: "3px" }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: "bold", color: "#333" }}>
                El cliente es responsable por entender y averiguar sobre la descripción del producto.
              </Typography>
              <Typography variant="body2" sx={{ color: "#333" }}>
                No nos hacemos responsables si realizas una compra sin leer detalladamente la descripción.{" "}
                <Button
                  onClick={() => navigate("/terminos-condiciones")}
                  variant="text"
                  sx={{ color: "#1565C0", textTransform: "none", fontWeight: "bold", p: 0, minWidth: 0 }}
                >
                  Ver más en TYC
                </Button>
              </Typography>
            </Box>
            <IconButton sx={{ position: "absolute", right: 8, top: 8 }} onClick={() => setShowAlert(false)}>
              <Close />
            </IconButton>
          </Box>
        )}

        <Box sx={{ maxWidth: "100%", mx: "auto", mt: 4 }}>
          <Typography
            variant="body1"
            sx={{ color: "#000" }}
            component="div"
            dangerouslySetInnerHTML={{
              __html: String(product.description || "")
                .split("\n")
                .map((line) => formatDescription(line))
                .join("<br/>"),
            }}
          />
        </Box>
      </Box>

      <RelatedProduct productId={product.id} />
      <Shop isOpen={isCartOpen} toggleDrawer={() => setIsCartOpen(!isCartOpen)} />
    </>
  );
}
